import React, { useState, useEffect } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    CardText,
    Button,
    Badge,
    Input,
    InputGroup,
    InputGroupText,
    Spinner,
    Alert
} from 'reactstrap';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { supabase } from '../../lib/supabase';
import './BlogPage.scss';



const BlogPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    const [openCategories, setOpenCategories] = useState([]);
    const [showSidebar, setShowSidebar] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [blogPosts, setBlogPosts] = useState([]);
    const [sidebarItems, setSidebarItems] = useState([]);
    const [popularTags, setPopularTags] = useState([]);
    const [totalPosts, setTotalPosts] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    const location = useLocation();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const postsPerPage = 9;

    const category = params.category;
    const tag = params.tag;
    const author = params.author;
    const searchFromUrl = searchParams.get('search');

    // Fetch blog data from Supabase
    useEffect(() => {
        const fetchBlogData = async () => {
            try {
                setLoading(true);
                setError(null);

                const offset = (currentPage - 1) * postsPerPage;

                // Build main posts query
                let query = supabase
                    .from('blog_posts')
                    .select(`
                        *,
                        author:blog_authors(*),
                        category:blog_categories(*),
                        tags:blog_post_tags(
                            tag:blog_tags(*)
                        )
                    `, { count: 'exact' })
                    .eq('is_published', true)
                    .order('published_at', { ascending: false });

                // Apply filters
                const searchTerm = searchQuery || searchFromUrl;
                if (searchTerm) {
                    query = query.or(`title.ilike.%${searchTerm}%,excerpt.ilike.%${searchTerm}%`);
                }

                if (category) {
                    query = query.eq('category.slug', category);
                }

                if (tag) {
                    query = query.contains('tags', [{ tag: { slug: tag } }]);
                }

                if (author) {
                    query = query.eq('author.slug', author);
                }

                // Execute query with pagination
                const { data: posts, error: postsError, count } = await query
                    .range(offset, offset + postsPerPage - 1);

                if (postsError) throw postsError;

                // Get categories for sidebar
                const { data: categories, error: categoriesError } = await supabase
                    .from('blog_categories')
                    .select(`
                        *,
                        blog_posts(count)
                    `)
                    .eq('is_active', true)
                    .order('sort_order');

                if (categoriesError) throw categoriesError;

                // Get popular tags
                const { data: tagsData, error: tagsError } = await supabase
                    .from('blog_tags')
                    .select('*')
                    .eq('is_popular', true)
                    .limit(10);

                if (tagsError) throw tagsError;

                // Format posts
                const formattedPosts = posts.map(post => ({
                    id: post.id,
                    title: post.title,
                    slug: post.slug,
                    excerpt: post.excerpt,
                    content: post.content,
                    featured_image: post.featured_image,
                    author_name: post.author?.name,
                    author_slug: post.author?.slug,
                    author_title: post.author?.title,
                    author_avatar: post.author?.avatar_url,
                    category_name: post.category?.name,
                    category_slug: post.category?.slug,
                    tags: (post.tags || []).map(t => t.tag).filter(t => t),
                    is_featured: post.is_featured,
                    read_time_minutes: post.read_time_minutes,
                    view_count: post.view_count,
                    published_at: post.published_at,
                    created_at: post.created_at,
                }));

                // Format categories with counts
                const formattedCategories = categories.map(cat => ({
                    id: cat.id,
                    name: cat.name,
                    slug: cat.slug,
                    description: cat.description,
                    icon: cat.icon,
                    count: cat.blog_posts?.[0]?.count || 0,
                }));

                if (currentPage === 1) {
                    setBlogPosts(formattedPosts);
                } else {
                    setBlogPosts(prev => [...prev, ...formattedPosts]);
                }

                setTotalPosts(count);
                setHasMore(formattedPosts.length === postsPerPage);

                if (currentPage === 1) {
                    setSidebarItems(formattedCategories);
                    setPopularTags(tagsData || []);
                }

            } catch (err) {
                console.error('Error fetching blog data:', err);
                setError('Failed to load blog posts. Please try again later.');

                // Fallback to empty data
                if (currentPage === 1) {
                    setBlogPosts([]);
                    setSidebarItems([]);
                    setPopularTags([]);
                }
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        };

        fetchBlogData();
    }, [searchQuery, currentPage, category, tag, author, searchFromUrl]);

    // Auto-open category based on current route
    useEffect(() => {
        if (category && !openCategories.includes(category)) {
            setOpenCategories([...openCategories, category]);
        }
    }, [category]);

    const toggleCategory = (categoryId) => {
        if (openCategories.includes(categoryId)) {
            setOpenCategories(openCategories.filter(id => id !== categoryId));
        } else {
            setOpenCategories([...openCategories, categoryId]);
        }
    };

    const toggleSidebar = () => {
        setShowSidebar(!showSidebar);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        // Reset to first page when searching
        setCurrentPage(1);
        setBlogPosts([]);
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        setCurrentPage(1);
        setBlogPosts([]);
    };

    const handleLoadMore = () => {
        setLoadingMore(true);
        setCurrentPage(prev => prev + 1);
    };

    // Get featured posts
    const featuredPosts = blogPosts.filter(post => post.is_featured);
    const regularPosts = blogPosts.filter(post => !post.is_featured);

    // Display posts (featured first, then regular)
    const displayPosts = currentPage === 1 ? [...featuredPosts, ...regularPosts] : blogPosts;

    if (loading && currentPage === 1) {
        return (
            <div className="blog-page">
                <Container className="py-5">
                    <div className="text-center">
                        <Spinner color="primary" size="lg" />
                        <p className="mt-3">Loading blog posts...</p>
                    </div>
                </Container>
            </div>
        );
    }

    return (
        <div className="blog-page">
            {/* Mobile Menu Button */}
            <div className="mobile-menu-button d-lg-none">
                <Button
                    color="primary"
                    onClick={toggleSidebar}
                    className="sidebar-toggle-btn"
                >
                    <i className="fas fa-filter mr-2"></i>
                    Filter Posts
                </Button>
            </div>

            <Container>
                {/* Error Display */}
                {error && (
                    <Alert color="danger" className="mt-3">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        {error}
                    </Alert>
                )}

                {/* Hero Section */}
                <div className="blog-hero py-5">
                    <Row className="align-items-center">
                        <Col lg="8" className="mx-auto text-center">
                            <h1 className="display-4 fw-bold mb-4">
                                {category ? `${category.charAt(0).toUpperCase() + category.slice(1)} Articles` :
                                    tag ? `#${tag} Articles` :
                                        author ? `Articles by ${author}` :
                                            'Reachtix Blog'}
                            </h1>
                            <p className="lead mb-4">
                                {category ? `Latest ${category} insights and strategies` :
                                    tag ? `Articles tagged with ${tag}` :
                                        author ? `All articles written by ${author}` :
                                            'Expert insights, strategies, and updates from the world of digital marketing'}
                            </p>
                            <form onSubmit={handleSearch} className="search-box mx-auto" style={{ maxWidth: '600px' }}>

                                <input
                                    type="text"
                                    placeholder="Search ..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                {(searchQuery || searchFromUrl) && (
                                    <Button
                                        color="link"
                                        type="button"
                                        onClick={handleClearSearch}
                                        className="clear-search"
                                    >
                                        Clear
                                    </Button>
                                )}

                            </form>
                            {(searchQuery || searchFromUrl) && displayPosts.length > 0 && (
                                <p className="mt-3 text-muted">
                                    Found {totalPosts} posts for "{searchQuery || searchFromUrl}"
                                </p>
                            )}
                        </Col>
                    </Row>
                </div>

                <Row className="blog-container">
                    {/* Sidebar Column */}
                    <Col lg="3" xl="3" className="d-none d-lg-block">
                        <div className="sidebar-column">
                            <div className="sidebar-wrapper">
                                <div className="sidebar-header">
                                    <h3 className="mb-3">
                                        <i className="fas fa-filter mr-2"></i>
                                        Categories
                                    </h3>
                                    <p className="text-muted mb-4">
                                        Filter by category
                                    </p>
                                </div>

                                {loading ? (
                                    <div className="text-center py-3">
                                        <Spinner size="sm" color="primary" />
                                    </div>
                                ) : (
                                    <>
                                        <div className="sidebar-content">
                                            {/* All Posts Link */}
                                            <Link
                                                to="/blog"
                                                className={`category-item ${!category && !tag && !author ? 'active' : ''}`}
                                            >
                                                <i className="fas fa-newspaper category-icon"></i>
                                                <span className="category-name">All Articles</span>
                                                <Badge color="light" pill className="ms-2">
                                                    {totalPosts}
                                                </Badge>
                                            </Link>

                                            {/* Featured Link */}
                                            <Link
                                                to="/blog?featured=true"
                                                className="category-item"
                                            >
                                                <i className="fas fa-star category-icon"></i>
                                                <span className="category-name">Featured</span>
                                            </Link>

                                            {/* Dynamic Categories */}
                                            {sidebarItems.map((categoryItem) => (
                                                <div
                                                    key={categoryItem.id}
                                                    className={`sidebar-category ${openCategories.includes(categoryItem.slug) ? 'open' : ''}`}
                                                >
                                                    <Link
                                                        to={`/blog/category/${categoryItem.slug}`}
                                                        className={`category-item ${category === categoryItem.slug ? 'active' : ''}`}
                                                    >
                                                        <i className={`${categoryItem.icon || 'fas fa-folder'} category-icon`}></i>
                                                        <span className="category-name">{categoryItem.name}</span>
                                                        {categoryItem.count > 0 && (
                                                            <Badge color="light" pill className="ms-2">
                                                                {categoryItem.count}
                                                            </Badge>
                                                        )}
                                                    </Link>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="sidebar-footer">
                                            {popularTags.length > 0 && (
                                                <div className="mb-4">
                                                    <h5 className="mb-3">Popular Tags</h5>
                                                    <div className="tags-container">
                                                        {popularTags.map((tagItem, index) => (
                                                            <Badge
                                                                key={index}
                                                                color="light"
                                                                className="me-2 mb-2 tag-badge"
                                                                tag={Link}
                                                                to={`/blog/tag/${tagItem.slug}`}
                                                            >
                                                                {tagItem.name}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            <Button
                                                tag={Link}
                                                to="/blog/subscribe"
                                                color="primary"
                                                outline
                                                block
                                            >
                                                <i className="fas fa-envelope mr-2"></i>
                                                Subscribe to Newsletter
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </Col>

                    {/* Mobile Sidebar */}
                    <div className={`sidebar-column d-lg-none ${showSidebar ? 'show' : ''}`}>
                        <div className="sidebar-wrapper">
                            <div className="sidebar-header">
                                <h3 className="mb-3">
                                    <i className="fas fa-filter mr-2"></i>
                                    Categories
                                </h3>
                                <button
                                    className="sidebar-close-btn"
                                    onClick={toggleSidebar}
                                    aria-label="Close sidebar"
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>

                            <div className="sidebar-content">
                                {/* Mobile Categories */}
                                <Link
                                    to="/blog"
                                    className={`category-item ${!category && !tag && !author ? 'active' : ''}`}
                                    onClick={() => setShowSidebar(false)}
                                >
                                    <i className="fas fa-newspaper category-icon"></i>
                                    <span className="category-name">All Articles</span>
                                </Link>

                                {sidebarItems.map((categoryItem) => (
                                    <div
                                        key={categoryItem.id}
                                        className="sidebar-category"
                                    >
                                        <Link
                                            to={`/blog/category/${categoryItem.slug}`}
                                            className={`category-item ${category === categoryItem.slug ? 'active' : ''}`}
                                            onClick={() => setShowSidebar(false)}
                                        >
                                            <i className={`${categoryItem.icon || 'fas fa-folder'} category-icon`}></i>
                                            <span className="category-name">{categoryItem.name}</span>
                                        </Link>
                                    </div>
                                ))}
                            </div>

                            <div className="sidebar-footer">
                                <Button
                                    tag={Link}
                                    to="/blog/subscribe"
                                    color="primary"
                                    outline
                                    block
                                    onClick={() => setShowSidebar(false)}
                                >
                                    <i className="fas fa-envelope mr-2"></i>
                                    Subscribe to Newsletter
                                </Button>
                            </div>
                        </div>
                        <div className="sidebar-overlay" onClick={toggleSidebar}></div>
                    </div>

                    {/* Main Content Column */}
                    <Col lg="9" xl="9">
                        <div className="main-content-column">
                            {/* Loading State */}
                            {loading && currentPage > 1 && (
                                <div className="text-center py-3">
                                    <Spinner color="primary" size="sm" />
                                    <p className="mt-2">Loading more posts...</p>
                                </div>
                            )}

                            {/* Error State */}
                            {error && !loading && (
                                <Alert color="warning" className="mb-4">
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                    {error}
                                    <div className="mt-2">
                                        <Button color="primary" size="sm" onClick={() => window.location.reload()}>
                                            Retry
                                        </Button>
                                    </div>
                                </Alert>
                            )}

                            {/* No Results State */}
                            {!loading && !error && displayPosts.length === 0 && (
                                <div className="no-results text-center py-5">
                                    <i className="fas fa-search fa-3x mb-3 text-muted"></i>
                                    <h4>No posts found</h4>
                                    <p className="text-muted mb-4">
                                        {searchQuery ? `No results for "${searchQuery}"` :
                                            category ? `No posts in "${category}" category` :
                                                tag ? `No posts tagged with "${tag}"` :
                                                    author ? `No posts by "${author}"` :
                                                        'No blog posts available yet.'}
                                    </p>
                                    <Button
                                        color="primary"
                                        outline
                                        onClick={handleClearSearch}
                                    >
                                        {searchQuery ? 'Clear Search' : 'Browse All Posts'}
                                    </Button>
                                </div>
                            )}

                            {/* Featured Posts Section */}
                            {!loading && !error && featuredPosts.length > 0 && currentPage === 1 && (
                                <section className="featured-posts mb-5">
                                    <h2 className="section-title mb-4">
                                        <i className="fas fa-star mr-2"></i>
                                        Featured Articles
                                    </h2>
                                    <Row>
                                        {featuredPosts.slice(0, 2).map((post) => (
                                            <Col lg="6" key={post.id} className="mb-4">
                                                <Card className="featured-post-card h-100">
                                                    <div className="post-image-wrapper">
                                                        <img
                                                            src={post.featured_image || 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'}
                                                            alt={post.title}
                                                            className="post-image"
                                                            loading="lazy"
                                                            onError={(e) => {
                                                                e.target.src = 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';
                                                            }}
                                                        />
                                                        <Badge color="primary" className="featured-badge">
                                                            Featured
                                                        </Badge>
                                                    </div>
                                                    <CardBody>
                                                        <div className="post-meta mb-3">
                                                            {post.category_name && (
                                                                <Badge color="light" className="me-2">
                                                                    {post.category_name}
                                                                </Badge>
                                                            )}
                                                            <span className="text-muted">
                                                                {post.published_at ? format(new Date(post.published_at), 'MMM d, yyyy') : 'Recently'} • {post.read_time_minutes || 5} min read
                                                            </span>
                                                        </div>
                                                        <CardTitle tag="h3" className="post-title">
                                                            {post.title}
                                                        </CardTitle>
                                                        <CardText className="post-excerpt">
                                                            {post.excerpt}
                                                        </CardText>
                                                        <div className="post-footer mt-4">
                                                            <span className="author text-muted">
                                                                By {post.author_name}
                                                            </span>
                                                            <Link
                                                                to={`/blog/${post.slug}`}
                                                                className="read-more-link"
                                                            >
                                                                Read More <i className="fas fa-arrow-right ms-1"></i>
                                                            </Link>
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                </section>
                            )}

                            {/* All Posts Section */}
                            {!loading && !error && displayPosts.length > 0 && (
                                <section className="all-posts">
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <h2 className="section-title mb-0">
                                            {category ? `${category.charAt(0).toUpperCase() + category.slice(1)} Articles` :
                                                tag ? `#${tag} Articles` :
                                                    'Latest Articles'}
                                        </h2>
                                        <div className="d-none d-md-block">
                                            <span className="text-muted">
                                                Showing {displayPosts.length} of {totalPosts} posts
                                            </span>
                                        </div>
                                    </div>

                                    <Row>
                                        {(currentPage === 1 ? regularPosts : displayPosts).map((post) => (
                                            <Col md="6" lg="6" xl="4" key={post.id} className="mb-4">
                                                <Card className="post-card h-100">
                                                    <div className="post-image-wrapper">
                                                        <img
                                                            src={post.featured_image || 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'}
                                                            alt={post.title}
                                                            className="post-image"
                                                            loading="lazy"
                                                            onError={(e) => {
                                                                e.target.src = 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';
                                                            }}
                                                        />
                                                    </div>
                                                    <CardBody>
                                                        <div className="post-meta mb-2">
                                                            {post.category_name && (
                                                                <Badge color="light" className="me-2">
                                                                    {post.category_name}
                                                                </Badge>
                                                            )}
                                                            <span className="text-muted">
                                                                {post.published_at ? format(new Date(post.published_at), 'MMM d, yyyy') : 'Recently'}
                                                            </span>
                                                        </div>
                                                        <CardTitle tag="h4" className="post-title">
                                                            {post.title}
                                                        </CardTitle>
                                                        <CardText className="post-excerpt">
                                                            {post.excerpt}
                                                        </CardText>
                                                        <div className="post-footer mt-3">
                                                            <div className="post-tags">
                                                                {post.tags?.slice(0, 2).map((tag, index) => (
                                                                    <Badge key={index} color="light" className="me-1 mb-1">
                                                                        {tag.name}
                                                                    </Badge>
                                                                ))}
                                                                {post.tags?.length > 2 && (
                                                                    <Badge color="light" className="mb-1">
                                                                        +{post.tags.length - 2}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                            <Link
                                                                to={`/blog/${post.slug}`}
                                                                className="read-more-link"
                                                            >
                                                                Read <i className="fas fa-arrow-right ms-1"></i>
                                                            </Link>
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>

                                    {/* Load More Button */}
                                    {hasMore && !loadingMore && (
                                        <div className="text-center mt-5">
                                            <Button
                                                color="primary"
                                                outline
                                                size="lg"
                                                onClick={handleLoadMore}
                                            >
                                                Load More Articles
                                            </Button>
                                        </div>
                                    )}

                                    {loadingMore && (
                                        <div className="text-center mt-3">
                                            <Spinner color="primary" size="sm" />
                                            <p className="mt-2">Loading more articles...</p>
                                        </div>
                                    )}

                                    {!hasMore && displayPosts.length > 0 && (
                                        <div className="text-center mt-5">
                                            <p className="text-muted">You've reached the end of the list</p>
                                        </div>
                                    )}
                                </section>
                            )}

                            {/* Newsletter CTA Section */}
                            <section className="newsletter-cta mt-5 py-5 px-4 rounded-3">
                                <Row className="align-items-center">
                                    <Col lg="8">
                                        <h3 className="mb-3">Stay Updated with Digital Marketing Trends</h3>
                                        <p className="mb-0">
                                            Get the latest digital marketing insights, strategies, and case studies delivered directly to your inbox.
                                            Join thousands of marketers who read our newsletter.
                                        </p>
                                    </Col>
                                    <Col lg="4" className="text-lg-end mt-3 mt-lg-0">
                                        <Button
                                            tag={Link}
                                            to="/blog/subscribe"
                                            color="primary"
                                            size="lg"
                                            className="px-4"
                                        >
                                            <i className="fas fa-envelope mr-2"></i>
                                            Subscribe Now
                                        </Button>
                                    </Col>
                                </Row>
                            </section>
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default BlogPage;